// pages/formularios/regras.js — tipos de bloco, limites da Meta e conferência.
//
// Out/26 (Formulários, PR 1). Serve pra AVISAR enquanto a pessoa monta: o
// contador "13/20" e o "Adicione pelo menos 2 opções" aparecem na hora, não
// depois de clicar em Publicar. A autoridade é o backend
// (`services/flow_json.validar`), que confere de novo antes de falar com a
// Meta — mesmas regras, mesmas frases. Mudou um limite lá, muda aqui.

export const TIPOS = [
  { v: 'texto', l: 'Texto curto', grupo: 'Perguntas' },
  { v: 'area', l: 'Texto longo', grupo: 'Perguntas' },
  { v: 'email', l: 'E-mail', grupo: 'Perguntas' },
  { v: 'telefone', l: 'Telefone', grupo: 'Perguntas' },
  { v: 'numero', l: 'Número', grupo: 'Perguntas' },
  { v: 'unica', l: 'Escolha única', grupo: 'Escolhas' },
  { v: 'multipla', l: 'Múltipla escolha', grupo: 'Escolhas' },
  { v: 'lista', l: 'Lista suspensa', grupo: 'Escolhas' },
  { v: 'data', l: 'Data', grupo: 'Datas' },
  { v: 'calendario', l: 'Calendário', grupo: 'Datas' },
  { v: 'aceite', l: 'Aceite (opt-in)', grupo: 'Aceite e anexos' },
  { v: 'foto', l: 'Foto do cliente', grupo: 'Aceite e anexos' },
  { v: 'documento', l: 'Documento do cliente', grupo: 'Aceite e anexos' },
  { v: 'conteudo', l: 'Texto', grupo: 'Conteúdo' },
  { v: 'imagem', l: 'Imagem', grupo: 'Conteúdo' },
];
export const GRUPOS = ['Perguntas', 'Escolhas', 'Datas', 'Aceite e anexos', 'Conteúdo'];
export const NOME_TIPO = Object.fromEntries(TIPOS.map((t) => [t.v, t.l]));

export const ESCOLHAS = ['unica', 'multipla', 'lista'];
export const ANEXOS = ['foto', 'documento'];
export const CONTEUDO = ['conteudo', 'imagem'];
export const ehResposta = (tipo) => !CONTEUDO.includes(tipo);

export const ESTILOS_TEXTO = [
  { v: 'titulo', l: 'Título', max: 80 },
  { v: 'subtitulo', l: 'Subtítulo', max: 80 },
  { v: 'paragrafo', l: 'Parágrafo', max: 4096 },
  { v: 'legenda', l: 'Legenda', max: 409 },
];
const MAX_ESTILO = Object.fromEntries(ESTILOS_TEXTO.map((e) => [e.v, e.max]));

// `anexos`: 10, não os 30 do componente — a resposta leva no máximo 10
// arquivos (PR 2). Mesmo valor de flow_json.ANEXOS_MAX.
export const LIMITE = {
  telas: 10, blocos: 49, tituloTela: 30, rodape: 35, opcao: 30,
  imagensPorTela: 3, imagemBytes: 300 * 1024, anexos: 10,
};
const ROTULO = {
  texto: 20, email: 20, telefone: 20, numero: 20, area: 20,
  data: 20, calendario: 20, lista: 20,
  unica: 30, multipla: 30, aceite: 120, foto: 30, documento: 30,
};
export const limiteRotulo = (tipo) => ROTULO[tipo] || 20;
export const limiteTexto = (estilo) => MAX_ESTILO[estilo] || 4096;
const OPCOES_MAX = { unica: 20, multipla: 20, lista: 200 };
export const maxOpcoes = (tipo) => OPCOES_MAX[tipo] || 20;

// Categorias da Meta que a tela oferece. "Login" fica de fora: pede um
// sistema do cliente pra conferir senha, o que formulário simples não tem.
export const CATEGORIAS = [
  { v: 'LEAD_GENERATION', l: 'Geração de leads' },
  { v: 'SIGN_UP', l: 'Cadastro' },
  { v: 'APPOINTMENT_BOOKING', l: 'Agendamento' },
  { v: 'CONTACT_US', l: 'Fale conosco' },
  { v: 'CUSTOMER_SUPPORT', l: 'Atendimento' },
  { v: 'SURVEY', l: 'Pesquisa' },
  { v: 'OTHER', l: 'Outro' },
];
export const NOME_CATEGORIA = Object.fromEntries(CATEGORIAS.map((c) => [c.v, c.l]));

// Status da Meta, com o nome e o tom da tela.
export const STATUS = {
  DRAFT: { l: 'Rascunho', tom: 'amber' },
  PUBLISHED: { l: 'Publicado', tom: 'green' },
  THROTTLED: { l: 'Limitado', tom: 'amber' },
  BLOCKED: { l: 'Bloqueado', tom: 'red' },
  DEPRECATED: { l: 'Descontinuado', tom: 'gray' },
};

let seq = 0;
export const novoId = () => `b${Date.now().toString(36)}${(seq += 1).toString(36)}${Math.random().toString(36).slice(2, 6)}`;

export function novoBloco(tipo) {
  const id = novoId();
  if (tipo === 'conteudo') return { id, tipo, estilo: 'paragrafo', texto: '' };
  if (tipo === 'imagem') return { id, tipo, src: '', largura: 0, altura: 0, descricao: '' };
  const b = { id, tipo, rotulo: '', obrigatorio: tipo !== 'foto' && tipo !== 'documento', destino: 'campo' };
  if (ESCOLHAS.includes(tipo)) b.opcoes = ['', ''];
  if (ANEXOS.includes(tipo)) { b.maximo = 1; b.descricao = ''; b.destino = 'nenhum'; }
  return b;
}

/** Troca o tipo de um bloco guardando o que ainda faz sentido. */
export function trocarTipo(bloco, tipo) {
  const novo = novoBloco(tipo);
  const fica = { id: bloco.id };
  if (ehResposta(tipo) && ehResposta(bloco.tipo)) {
    fica.rotulo = bloco.rotulo || '';
    if (!ANEXOS.includes(tipo)) fica.obrigatorio = bloco.obrigatorio;
    if (ESCOLHAS.includes(tipo) && bloco.opcoes?.length) fica.opcoes = bloco.opcoes;
    if (!ANEXOS.includes(tipo) && bloco.destino && !(bloco.destino === 'nome' && tipo !== 'texto')) fica.destino = bloco.destino;
  }
  return { ...novo, ...fica, tipo };
}

export function bytesDaImagem(src) {
  const m = /^data:image\/(jpeg|jpg|png);base64,(.+)$/.exec(src || '');
  if (!m) return null;
  const b64 = m[2];
  const pad = b64.endsWith('==') ? 2 : b64.endsWith('=') ? 1 : 0;
  return Math.floor((b64.length * 3) / 4) - pad;
}

const livre = (t) => !String(t || '').includes('${');
const p = (tela, bloco, campo, mensagem) => ({ tela, bloco, campo, mensagem });

/** Mesma conferência do backend. Lista vazia = pode publicar. */
export function validar(def) {
  const probs = [];
  const telas = def?.telas || [];
  if (!telas.length) return [p(null, null, null, 'O formulário precisa de pelo menos uma tela.')];
  if (telas.length > LIMITE.telas) probs.push(p(null, null, null, `O formulário tem ${telas.length} telas; o limite é ${LIMITE.telas}.`));
  const vistos = new Set();
  let nomeMapeado = false;
  const ultima = telas.length - 1;
  telas.forEach((tela, i) => {
    const titulo = (tela.titulo || '').trim();
    if (!titulo) probs.push(p(i, null, 'titulo', 'Dê um título à tela.'));
    else if (titulo.length > LIMITE.tituloTela) probs.push(p(i, null, 'titulo', `O título da tela passa de ${LIMITE.tituloTela} caracteres.`));
    else if (!livre(titulo)) probs.push(p(i, null, 'titulo', 'Tire o “${” do título: o WhatsApp lê isso como variável.'));
    const rodape = (tela.rodape || '').trim();
    if (!rodape) probs.push(p(i, null, 'rodape', 'Escreva o texto do botão do rodapé.'));
    else if (rodape.length > LIMITE.rodape) probs.push(p(i, null, 'rodape', `O botão do rodapé passa de ${LIMITE.rodape} caracteres.`));
    const blocos = tela.blocos || [];
    if (!blocos.length) probs.push(p(i, null, null, 'A tela está vazia. Adicione uma pergunta ou um texto.'));
    if (blocos.length > LIMITE.blocos) probs.push(p(i, null, null, `Cada tela aceita até ${LIMITE.blocos} blocos.`));
    let imagens = 0; let fotos = 0; let docs = 0;
    blocos.forEach((b) => {
      if (vistos.has(b.id)) probs.push(p(i, b.id, null, 'Bloco repetido. Apague e adicione de novo.'));
      vistos.add(b.id);
      if (b.tipo === 'conteudo') {
        const max = limiteTexto(b.estilo || 'paragrafo');
        const t = (b.texto || '').trim();
        if (!t) probs.push(p(i, b.id, 'texto', 'Escreva o texto ou apague o bloco.'));
        else if (t.length > max) probs.push(p(i, b.id, 'texto', `O texto passa de ${max} caracteres.`));
        else if (!livre(t)) probs.push(p(i, b.id, 'texto', 'Tire o “${” do texto: o WhatsApp lê isso como variável.'));
        return;
      }
      if (b.tipo === 'imagem') {
        imagens += 1;
        const n = bytesDaImagem(b.src);
        if (n == null) probs.push(p(i, b.id, 'src', 'Escolha uma imagem JPG ou PNG.'));
        else if (n > LIMITE.imagemBytes) probs.push(p(i, b.id, 'src', 'A imagem passa de 300 KB. Use uma menor.'));
        return;
      }
      const rotulo = (b.rotulo || '').trim();
      const max = limiteRotulo(b.tipo);
      if (!rotulo) probs.push(p(i, b.id, 'rotulo', 'Escreva o rótulo da pergunta.'));
      else if (rotulo.length > max) probs.push(p(i, b.id, 'rotulo', `O rótulo passa de ${max} caracteres.`));
      else if (!livre(rotulo)) probs.push(p(i, b.id, 'rotulo', 'Tire o “${” do rótulo: o WhatsApp lê isso como variável.'));
      if (b.destino === 'nome') {
        if (b.tipo !== 'texto') probs.push(p(i, b.id, 'destino', 'Só um campo de texto curto pode ir para o nome do contato.'));
        else if (nomeMapeado) probs.push(p(i, b.id, 'destino', 'Outro campo já vai para o nome do contato.'));
        nomeMapeado = true;
      }
      if (ESCOLHAS.includes(b.tipo)) {
        const ops = (b.opcoes || []).map(String);
        const mx = maxOpcoes(b.tipo);
        if (ops.length < 2) probs.push(p(i, b.id, 'opcoes', 'Adicione pelo menos 2 opções.'));
        else if (ops.length > mx) probs.push(p(i, b.id, 'opcoes', `São ${ops.length} opções; o WhatsApp aceita ${mx}.${b.tipo !== 'lista' ? ' Use uma lista suspensa.' : ''}`));
        const vistas = new Set();
        ops.forEach((op) => {
          const t = op.trim();
          if (!t) probs.push(p(i, b.id, 'opcoes', 'Há uma opção em branco.'));
          else if (t.length > LIMITE.opcao) probs.push(p(i, b.id, 'opcoes', `A opção “${t.slice(0, 20)}…” passa de ${LIMITE.opcao} caracteres.`));
          else if (vistas.has(t.toLowerCase())) probs.push(p(i, b.id, 'opcoes', `A opção “${t}” está repetida.`));
          else if (!livre(t)) probs.push(p(i, b.id, 'opcoes', 'Tire o “${” da opção: o WhatsApp lê isso como variável.'));
          vistas.add(t.toLowerCase());
        });
      }
      if (ANEXOS.includes(b.tipo)) {
        if (b.tipo === 'foto') fotos += 1; else docs += 1;
        if (i !== ultima) probs.push(p(i, b.id, null, 'Foto e documento só podem ficar na última tela.'));
        const mx = b.maximo || 1;
        if (!Number.isInteger(mx) || mx < 1 || mx > LIMITE.anexos) probs.push(p(i, b.id, 'maximo', `Escolha de 1 a ${LIMITE.anexos} arquivos.`));
      }
    });
    if (imagens > LIMITE.imagensPorTela) probs.push(p(i, null, null, `Cada tela aceita até ${LIMITE.imagensPorTela} imagens.`));
    if (fotos && docs) probs.push(p(i, null, null, 'Foto e documento não podem ficar na mesma tela.'));
    else if (fotos > 1 || docs > 1) probs.push(p(i, null, null, 'Cada tela aceita um campo de foto ou um de documento.'));
  });
  return probs;
}

/** Blocos de resposta na ordem, com a tela — base de "Respostas no CRM". */
export function respostas(def) {
  const out = [];
  (def?.telas || []).forEach((tela, i) => {
    (tela.blocos || []).forEach((b) => { if (ehResposta(b.tipo)) out.push({ tela: i, bloco: b }); });
  });
  return out;
}

export function resumo(def) {
  const telas = def?.telas || [];
  const campos = respostas(def).length;
  return `${telas.length} tela${telas.length === 1 ? '' : 's'} · ${campos} campo${campos === 1 ? '' : 's'}`;
}

/** Imagem → JPEG ≤ 300 KB (lado maior até 900 px), como data URL. */
export async function comprimirImagem(file) {
  const url = URL.createObjectURL(file);
  try {
    const img = await new Promise((ok, erro) => {
      const el = new Image();
      el.onload = () => ok(el);
      el.onerror = () => erro(new Error('imagem'));
      el.src = url;
    });
    let lado = 900;
    for (let tentativa = 0; tentativa < 6; tentativa += 1) {
      const escala = Math.min(1, lado / Math.max(img.width, img.height));
      const w = Math.max(1, Math.round(img.width * escala));
      const h = Math.max(1, Math.round(img.height * escala));
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      ctx.fillStyle = '#ffffff';
      ctx.fillRect(0, 0, w, h);
      ctx.drawImage(img, 0, 0, w, h);
      for (const q of [0.85, 0.72, 0.6]) {
        const src = canvas.toDataURL('image/jpeg', q);
        if (bytesDaImagem(src) <= LIMITE.imagemBytes) return { src, largura: w, altura: h };
      }
      lado = Math.round(lado * 0.75);
    }
    throw new Error('grande');
  } finally {
    URL.revokeObjectURL(url);
  }
}
